import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CardContainer,
  DataGrid,
  GridColDef,
  InfoLabelMessage,
  ModalOpenButton,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { IconButton, Stack, Switch, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { OrganisationWorkflowPanel } from "../modals/OrganisationWorkflowPanel.js";
import {
  OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment,
  useDeleteOrganisationWorkflowMutation,
  useSetOrganisationWorkflowActiveMutation,
  useWorkflowTypeDefinitionsQuery,
} from "./OrganisationAutomatedWorkflowsBox.generated.js";

interface Props {
  workflows: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment[];
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  organisationId?: string;
}

export function OrganisationAutomatedWorkflowsBox({
  workflows,
  emailTemplates,
  organisationId,
}: Props) {
  const { t } = useTranslate(["OrganisationSettings"]);

  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: typeDefsData } = useWorkflowTypeDefinitionsQuery({ client });
  const workflowTypeDefs =
    getDataOrNull(typeDefsData?.workflowTypeDefinitions)
      ?.workflowTypeDefinitions ?? [];

  const workflowTypeTitles = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const def of workflowTypeDefs) {
      map[def.workflowType] = def.label;
    }
    return map;
  }, [workflowTypeDefs]);

  const workflowTypeHasEmailTemplate = React.useCallback(
    (workflowType: string) =>
      workflowTypeDefs
        .find(d => d.workflowType === workflowType)
        ?.configFields.some(f => f.fieldType === "emailTemplatePicker") ??
      false,
    [workflowTypeDefs]
  );

  const [setWorkflowActive] = useSetOrganisationWorkflowActiveMutation({
    client,
  });

  const [deleteWorkflow] = useDeleteOrganisationWorkflowMutation({
    client,
    refetchQueries: [namedOperations.Query.OrganisationWorkflows],
  });

  const handleToggle = React.useCallback(
    async (workflowId: string, active: boolean) => {
      try {
        await setWorkflowActive({
          variables: { workflowId, active },
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [setWorkflowActive, enqueueSnackbar]
  );

  const columns: GridColDef<OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment>[] =
    React.useMemo(
      () => [
        {
          field: "workflowType",
          headerName: t("Type" as any, { ns: "OrganisationSettings" }),
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => {
            const emailTemplateId = (row.config as Record<string, unknown>)
              ?.emailTemplateId as string | undefined;
            const needsEmailTemplate = workflowTypeHasEmailTemplate(
              row.workflowType
            );
            const emailTemplateWarning =
              needsEmailTemplate && !emailTemplateId
                ? t("Email template missing" as any, {
                    ns: "OrganisationSettings",
                  })
                : needsEmailTemplate &&
                    emailTemplateId &&
                    !emailTemplates.some(et => et.id === emailTemplateId)
                  ? t("Email template no longer exists" as any, {
                      ns: "OrganisationSettings",
                    })
                  : null;
            return (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: "center",
                }}
              >
                {emailTemplateWarning && (
                  <Tooltip title={emailTemplateWarning}>
                    <WarningIcon color="warning" fontSize="small" />
                  </Tooltip>
                )}
                <span>
                  {t(
                    (workflowTypeTitles[row.workflowType] ??
                      row.workflowType) as any,
                    { ns: "OrganisationSettings" }
                  )}
                </span>
              </Stack>
            );
          },
        },
        {
          field: "description",
          headerName: t("Description", { ns: "OrganisationSettings" }),
          flex: 1,
          sortable: false,
        },
        {
          field: "active",
          headerName: t("Active" as any, { ns: "OrganisationSettings" }),
          width: 80,
          sortable: false,
          renderCell: ({ row }) => (
            <Switch
              checked={row.active}
              onChange={(_e, checked) => handleToggle(row.id, checked)}
              size="small"
            />
          ),
        },
        {
          field: "actions",
          headerName: "",
          width: 92,
          sortable: false,
          renderCell: ({ row }) => (
            <Stack direction="row" spacing={0.5}>
              <ModalOpenButton
                Modal={OrganisationWorkflowPanel}
                modalProps={{
                  workflow: row,
                  workflows,
                  emailTemplates,
                  organisationId,
                }}
              >
                <IconButton color="primary" size="small">
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
              <ModalOpenButton
                Modal={ConfirmModal}
                modalProps={{
                  handleConfirm: async () => {
                    await deleteWorkflow({
                      variables: { workflowId: row.id },
                    });
                  },
                }}
              >
                <IconButton color="primary" size="small">
                  <DeleteIcon />
                </IconButton>
              </ModalOpenButton>
            </Stack>
          ),
        },
      ],
      [
        t,
        handleToggle,
        emailTemplates,
        organisationId,
        deleteWorkflow,
        workflowTypeTitles,
        workflowTypeHasEmailTemplate,
        workflows,
      ]
    );

  return (
    <CardContainer
      title={t("Automated workflows", {
        ns: "OrganisationSettings",
      })}
      isExpandable
      ActionButton={
        <ModalOpenButton
          Modal={OrganisationWorkflowPanel}
          modalProps={{
            workflows,
            emailTemplates,
            organisationId,
          }}
        >
          <IconButton color="primary">
            <AddIcon />
          </IconButton>
        </ModalOpenButton>
      }
    >
      <Stack
        spacing={1}
        sx={{
          padding: 1,
        }}
      >
        <InfoLabelMessage
          message={t(
            "These are automated workflows which are triggered by domain events. You can configure which events trigger each workflow and optionally filter by source type.",
            { ns: "OrganisationSettings" }
          )}
        />
        <DataGrid
          columns={columns}
          rows={workflows}
          hideFooter
          paginationMode="client"
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          sx={{ "& .MuiDataGrid-row.MuiDataGrid-row": { cursor: "default" } }}
        />
      </Stack>
    </CardContainer>
  );
}
